import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, MoreVertical, ArrowUpRight, Circle, ArrowDownUp } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../../components/ui/DropdownMenu';
import { Waveform } from '../../components/Waveform';
import { freesoundCollections, freesoundSounds } from '../../src/seed';
export function FreesoundCollections() {
  const navigate = useNavigate();
  const totalSounds = freesoundCollections.reduce((sum, c) => sum + c.itemCount, 0);
  return <div className="max-w-[1100px] mx-auto pb-24" data-id="element-184">
      {/* Editorial header */}
      <header className="mb-14" data-id="element-185">
        <div className="flex items-start justify-between gap-8 mb-12" data-id="element-186">
          <div className="max-w-2xl" data-id="element-187">
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono mb-5 flex items-center gap-2" data-id="element-188">
              <Circle className="size-1.5 text-emerald-500 fill-emerald-500" strokeWidth={0} data-id="element-189" />
              Freesound / Library
            </p>
            <h1 className="font-serif text-[64px] font-medium tracking-[-0.025em] leading-[0.92] text-foreground" data-id="element-190">
              Collections
            </h1>
            <p className="mt-6 text-[15px] leading-[1.65] text-muted-foreground max-w-[52ch]" data-id="element-191">
              Curated groups of clinical audio — heart sounds, respiratory
              patterns, environmental context, and equipment alarms — organised
              for fast retrieval during simulation, bedside teaching, and
              assessment design.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0 pt-3" data-id="element-192">
            <Button variant="outline" size="sm" className="h-9 rounded-full px-4 text-[12px] font-medium gap-1.5" data-id="element-193">
              <ArrowDownUp className="size-3" strokeWidth={2.5} data-id="element-194" />
              Sort
            </Button>
            <Button size="sm" className="h-9 rounded-full px-4 text-[12px] font-medium gap-1.5" data-id="element-195">
              <Plus className="size-3.5" strokeWidth={2.5} data-id="element-196" />
              New collection
            </Button>
          </div>
        </div>

        {/* Stat strip */}
        <dl className="flex items-end gap-14 pt-8 border-t border-border" data-id="element-197">
          <div data-id="element-198">
            <dt className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground" data-id="element-199">
              Collections
            </dt>
            <dd className="font-serif text-[40px] font-medium text-foreground tabular-nums leading-none tracking-[-0.02em]" data-id="element-200">
              {freesoundCollections.length.toString().padStart(2, '0')}
            </dd>
          </div>
          <div className="h-12 w-px bg-border" data-id="element-201" />
          <div data-id="element-202">
            <dt className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground" data-id="element-203">
              Sounds indexed
            </dt>
            <dd className="font-serif text-[40px] font-medium text-foreground tabular-nums leading-none tracking-[-0.02em]" data-id="element-204">
              {totalSounds}
            </dd>
          </div>
          <div className="h-12 w-px bg-border" data-id="element-205" />
          <div data-id="element-206">
            <dt className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground" data-id="element-207">
              Source type
            </dt>
            <dd className="font-serif text-[28px] font-medium text-foreground leading-none tracking-[-0.01em] pt-[6px]" data-id="element-208">
              Manual curation
            </dd>
          </div>
          <div className="ml-auto flex items-center gap-2 pb-1 text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground" data-id="element-209">
            <span className="inline-flex size-1.5 rounded-full bg-emerald-500 animate-pulse" data-id="element-210" />
            Live — last indexed 3m ago
          </div>
        </dl>
      </header>

      {/* Collection grid — 2 cols, magazine density */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-14" data-id="element-211">
        {freesoundCollections.map((collection, idx) => {
        const referenced = freesoundSounds.filter(s => s.collectionIds.includes(collection.id)).length;
        const indexLabel = String(idx + 1).padStart(2, '0');
        return <article key={collection.id} onClick={() => navigate(`/freesound/collections/${collection.id}`)} className="group cursor-pointer pt-6 border-t-2 border-foreground" data-id="element-212">
              {/* Index + meta */}
              <div className="flex items-start justify-between mb-7" data-id="element-213">
                <div className="flex items-baseline gap-4 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground" data-id="element-214">
                  <span className="tabular-nums text-foreground" data-id="element-215">
                    № {indexLabel}
                  </span>
                  <span data-id="element-216">Manual</span>
                  <span className="tabular-nums" data-id="element-217">
                    {collection.itemCount} sounds
                  </span>
                </div>
                <DropdownMenu data-id="element-218">
                  <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()} data-id="element-219">
                    <button className="size-7 -mr-1 -mt-1 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100" aria-label="Collection actions" data-id="element-220">
                      <MoreVertical className="size-3.5" data-id="element-221" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" data-id="element-222">
                    <DropdownMenuItem onClick={e => e.stopPropagation()} data-id="element-223">
                      Open
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={e => e.stopPropagation()} data-id="element-224">
                      Rename
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={e => e.stopPropagation()} className="text-destructive focus:text-destructive" data-id="element-225">
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Title */}
              <h2 className="font-serif text-[34px] font-medium leading-[1.03] tracking-[-0.018em] mb-4 text-foreground group-hover:text-foreground" data-id="element-226">
                <span className="bg-[linear-gradient(to_right,currentColor,currentColor)] bg-[length:0%_1px] bg-no-repeat bg-[0_100%] group-hover:bg-[length:100%_1px] transition-[background-size] duration-500 ease-out" data-id="element-227">
                  {collection.name}
                </span>
              </h2>

              {/* Description */}
              <p className="text-[14px] leading-[1.6] text-muted-foreground mb-9 max-w-[46ch]" data-id="element-228">
                {collection.description}
              </p>

              {/* Waveform preview */}
              <div className="text-foreground/[0.18] group-hover:text-foreground/40 transition-colors duration-500 h-11 mb-5" data-id="element-229">
                <Waveform seed={collection.id} bars={120} thickness={2} rounded={false} data-id="element-230" />
              </div>

              {/* Meta strip */}
              <div className="flex items-center justify-between text-[11px] font-mono uppercase tracking-[0.12em] tabular-nums" data-id="element-231">
                <span className="flex items-center gap-2 text-muted-foreground" data-id="element-232">
                  <span className="inline-flex size-1.5 rounded-full bg-emerald-500" data-id="element-233" />
                  Ready · {referenced} indexed
                </span>
                <span className="flex items-center gap-1.5 text-foreground group-hover:gap-2.5 transition-all duration-300" data-id="element-234">
                  Browse
                  <ArrowUpRight className="size-3 group-hover:-translate-y-px group-hover:translate-x-px transition-transform" strokeWidth={2} data-id="element-235" />
                </span>
              </div>
            </article>;
      })}

        {/* Empty-state card */}
        <article className="group cursor-pointer pt-6 border-t-2 border-dashed border-foreground/25" data-id="element-236">
          <div className="flex items-start justify-between mb-7" data-id="element-237">
            <div className="flex items-baseline gap-4 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground" data-id="element-238">
              <span className="tabular-nums" data-id="element-239">
                № {String(freesoundCollections.length + 1).padStart(2, '0')}
              </span>
              <span data-id="element-240">New</span>
              <span className="tabular-nums" data-id="element-241">0 sounds</span>
            </div>
          </div>
          <h2 className="font-serif text-[34px] font-medium leading-[1.03] tracking-[-0.018em] mb-4 text-muted-foreground/70" data-id="element-242">
            Start a collection
          </h2>
          <p className="text-[14px] leading-[1.6] text-muted-foreground mb-9 max-w-[46ch]" data-id="element-243">
            Group saved Freesound audio by body system, clinical context, or
            teaching scenario. Collections feed straight into your simulation
            library.
          </p>
          <div className="text-foreground/[0.08] h-11 mb-5" data-id="element-244">
            <Waveform seed="empty-collection-placeholder" bars={120} thickness={2} rounded={false} data-id="element-245" />
          </div>
          <Button variant="outline" size="sm" className="h-9 rounded-full px-4 text-[12px] font-medium gap-1.5" data-id="element-246">
            <Plus className="size-3.5" strokeWidth={2.5} data-id="element-247" />
            Create collection
          </Button>
        </article>
      </div>
    </div>;
}